import { homePlanLockableByPhase } from "api-types/homePlans/homePlanLockEligibility";
import type { ClientCascadeLockPreview } from "api-types/homePlans/homePlanResponses";
import type { ClientHomePlanHydratedNoDoc } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { isMultiUnitLocation } from "api-types/locations/locationDisplay";
import { WEB_API_BASE_PATH } from "common/constants";
import {
  LOCK_HOME_PLAN_PREP_TOOLTIP,
  LOCK_UNIT_HOME_PLAN_LOT_AVAILABLE_TOOLTIP,
  UNLOCK_UNIT_HOME_PLAN_BUILDING_LOCKED_TOOLTIP,
} from "lib/constants/messages";
import { useFetch } from "lib/fetcher";
import { unitUnlockBlockedWhileBuildingLocked, userCanLockHomePlan, userCanUnlockHomePlan } from "lib/security/client";
import type { UserContextState } from "lib/state/UserContextProvider";

export function getHomePlanLockTooltip(
  homePlan: Pick<ClientHomePlanHydratedNoDoc, "planStatus" | "isMultiUnit" | "lot">,
  location?: ClientLocationWithLevel,
  cascadeLockPreview?: Pick<ClientCascadeLockPreview, "lockable" | "blockingMessage">,
): string | undefined {
  const atMultiUnitLocation = isMultiUnitLocation(location);
  if (
    !homePlanLockableByPhase({
      planStatus: homePlan.planStatus,
      isMultiUnit: !!homePlan.isMultiUnit,
      atMultiUnitLocation,
      lotStatus: homePlan.lot?.lotStatus,
    })
  ) {
    if (atMultiUnitLocation && !homePlan.isMultiUnit) {
      return LOCK_UNIT_HOME_PLAN_LOT_AVAILABLE_TOOLTIP;
    }
    return LOCK_HOME_PLAN_PREP_TOOLTIP;
  }
  if (homePlan.isMultiUnit && cascadeLockPreview && !cascadeLockPreview.lockable) {
    return cascadeLockPreview.blockingMessage;
  }
  return undefined;
}

/**
 * Table row overflow menus offer lock/unlock for building aggregates; that needs cascadeLockPreview
 * (lockable, blocking tooltip, unlock scope) - detail pages get preview from HomePlanContextProvider
 * Defer with enabled=false until the row menu or lock modal opens so rows do not each fetch on render
 * LockPlanModal uses the same URL and SWR will cache
 */
export function useAggregateCascadeLockPreview(
  builderId: string,
  homePlan: Pick<ClientHomePlanHydratedNoDoc, "id" | "isMultiUnit">,
  enabled = true,
): ClientCascadeLockPreview | undefined {
  const { data } = useFetch<{ cascadeLockPreview?: ClientCascadeLockPreview }>(
    enabled && homePlan.isMultiUnit
      ? `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/${homePlan.id}/base-data`
      : "",
    [builderId, homePlan.id, homePlan.isMultiUnit, enabled],
  );
  return data?.cascadeLockPreview;
}

interface UseHomePlanLockUnlockStateParams {
  builderId: string;
  homePlan: ClientHomePlanHydratedNoDoc;
  location?: ClientLocationWithLevel;
  userContext: Pick<UserContextState, "user" | "hasPermission">;
  /** Preloaded preview from HomePlanContext (detail page). Skips /base-data fetch when set. */
  cascadeLockPreview?: ClientCascadeLockPreview;
  /** When true and cascadeLockPreview is absent, fetches preview for building aggregates (e.g. table row menu open). */
  fetchCascadeLockPreview?: boolean;
}

export function useHomePlanLockUnlockState({
  builderId,
  homePlan,
  location,
  userContext,
  cascadeLockPreview: cascadeLockPreviewFromProps,
  fetchCascadeLockPreview = false,
}: UseHomePlanLockUnlockStateParams): {
  cascadeLockPreview: ClientCascadeLockPreview | undefined;
  homePlanLockable: boolean;
  lockTooltip: string | undefined;
  homePlanUnlockable: boolean;
  unlockTooltip: string | undefined;
} {
  const shouldFetchPreview = fetchCascadeLockPreview && cascadeLockPreviewFromProps === undefined;
  const fetchedCascadeLockPreview = useAggregateCascadeLockPreview(builderId, homePlan, shouldFetchPreview);
  const cascadeLockPreview = cascadeLockPreviewFromProps ?? fetchedCascadeLockPreview;

  const homePlanLockable = userCanLockHomePlan(userContext, homePlan, location, cascadeLockPreview);
  const lockTooltip = getHomePlanLockTooltip(homePlan, location, cascadeLockPreview);
  const homePlanUnlockable = userCanUnlockHomePlan(userContext, homePlan, location);
  const unlockTooltip = unitUnlockBlockedWhileBuildingLocked(homePlan)
    ? UNLOCK_UNIT_HOME_PLAN_BUILDING_LOCKED_TOOLTIP
    : undefined;

  return {
    cascadeLockPreview,
    homePlanLockable,
    lockTooltip,
    homePlanUnlockable,
    unlockTooltip,
  };
}
